import {Card, Empty} from 'antd';
import {useMemo} from 'react';

import {LineChart} from '../../../../../../layout/chart/line-chart/line-chart';
import {LineDataSetType, LineDataType} from '../../../../../../layout/chart/line-chart/line-chart-type';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {AiMonitoringScorePointType} from '../../../../../../service/ai-brand-monitoring/ai-brand-monitoring-type';
import {getDatasetColor} from '../../../../../../util/chart-colors/chart-colors';

import * as styles from './score-dynamics-chart.scss';

type PropsType = {
    series: Array<AiMonitoringScorePointType>;
    brandName?: string;
    // When provided, only these competitor ids (current config competitors) are charted.
    competitorIds?: Array<number>;
};

function buildLineData(
    series: Array<AiMonitoringScorePointType>,
    brandLabel: string,
    allowedIds: Set<number> | null
): LineDataType {
    const labels = series.map((point) => point.date);

    const brandDataset: LineDataSetType = {
        label: brandLabel,
        values: series.map((point) => point.brandScore ?? 0),
        color: getDatasetColor(0),
        width: 2,
    };

    // Stable competitor ordering by first appearance, keyed by competitor id.
    const competitorIds: Array<number> = [];
    const competitorNameById = new Map<number, string>();

    series.forEach((point) => {
        point.competitors.forEach((competitor) => {
            // Defensive against stale data: skip competitors with empty/blank names.
            if (competitor.name.trim().length === 0) {
                return;
            }

            // Only chart competitors that are in the current config.
            if (allowedIds && !allowedIds.has(competitor.id)) {
                return;
            }

            if (!competitorNameById.has(competitor.id)) {
                competitorNameById.set(competitor.id, competitor.name);
                competitorIds.push(competitor.id);
            }
        });
    });

    const competitorDatasets: Array<LineDataSetType> = competitorIds.map((competitorId, index) => ({
        label: competitorNameById.get(competitorId) ?? String(competitorId),
        values: series.map((point) => {
            const found = point.competitors.find((competitor) => competitor.id === competitorId);

            return found ? found.score : 0;
        }),
        color: getDatasetColor(index + 1),
        dashed: true,
        width: 1,
    }));

    return {
        labels,
        datasets: [brandDataset, ...competitorDatasets],
    };
}

export function ScoreDynamicsChart(props: PropsType): JSX.Element {
    const {series, brandName, competitorIds} = props;
    const {getLocalizedString} = useLocale();

    const brandLabel = brandName || getLocalizedString('AI_BRAND_MONITORING__DASHBOARD__BRAND_SCORE');

    const allowedIds = useMemo<Set<number> | null>(
        () => (competitorIds ? new Set(competitorIds) : null),
        [competitorIds]
    );

    const lineData = useMemo<LineDataType | null>(() => {
        if (series.length === 0) {
            return null;
        }

        return buildLineData(series, brandLabel, allowedIds);
    }, [series, brandLabel, allowedIds]);

    return (
        <Card size="small" title={<Locale stringKey="AI_BRAND_MONITORING__DASHBOARD__SCORE_DYNAMICS" />}>
            {lineData ? (
                <div className={styles.score_dynamics_chart__canvas}>
                    <LineChart data={lineData} />
                </div>
            ) : (
                <Empty description={getLocalizedString('AI_BRAND_MONITORING__DASHBOARD__NO_DATA')} />
            )}
        </Card>
    );
}
